import { Button } from "design-system/atoms/Button"
import { DialogFooter } from "design-system/atoms/Dialog"
import { Input } from "design-system/atoms/Input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { getErrorTranslations } from "lib/i18n"
import { joinName, languages } from "lopermut"
import { useCountries } from "modules/shared/hooks/useCountries"
import { UseFormReturn } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { UpsertCustomerBodySchema } from "schema"
import { z } from "zod"

type Props = {
  form: UseFormReturn<z.infer<typeof UpsertCustomerBodySchema>>
  onSubmit: (data: z.infer<typeof UpsertCustomerBodySchema>) => void
  submitCopy: string
}

export const BaseCustomerForm = ({ form, onSubmit, submitCopy }: Props) => {
  const { t } = useTranslation()
  const { options: countries } = useCountries()

  return (
    <Form
      {...form}
      onSubmit={form.handleSubmit((data) =>
        onSubmit({ ...data, name: joinName(data.firstName, data.lastName) }),
      )}
      className="flex flex-col gap-4"
      errorTranslations={getErrorTranslations()}
    >
      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="firstName"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <FormLabel>{t("customers.profile.firstName")}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  value={field.value?.toString()}
                  placeholder={t("customers.profile.firstNamePlaceholder")}
                  required
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="lastName"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <FormLabel>{t("customers.profile.lastName")}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  value={field.value?.toString()}
                  placeholder={t("customers.profile.lastNamePlaceholder")}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <FormField
        control={form.control}
        name="email"
        render={({ field }) => (
          <FormItem className="flex flex-col">
            <FormLabel>{t("customers.profile.email")}</FormLabel>
            <FormControl>
              <Input
                {...field}
                value={field.value?.toString()}
                type="email"
                placeholder={t("customers.profile.emailPlaceholder")}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="phone"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t("customers.profile.phone")}</FormLabel>
            <FormControl>
              <Input
                {...field}
                value={field.value?.toString()}
                type="tel"
                placeholder={t("customers.profile.phonePlaceholder")}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="locale"
        render={({ field }) => (
          <FormItem className="flex flex-col">
            <FormLabel>{t("customers.profile.language")}</FormLabel>
            <FormControl>
              <Select onValueChange={(value) => field.onChange(value)} value={field.value ?? ""}>
                <SelectTrigger>
                  <SelectValue placeholder={t("customers.profile.languagePlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  {languages.map((language) => (
                    <SelectItem key={language.value} value={language.value}>
                      {language.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="country"
        render={({ field }) => (
          <FormItem className="flex flex-col">
            <FormLabel>{t("customers.profile.country")}</FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value ?? ""}>
                <SelectTrigger>
                  <SelectValue placeholder={t("customers.profile.countryPlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  {countries.map(({ value, label }) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <DialogFooter>
        <Button type="submit" disabled={!form.formState.isValid}>
          {submitCopy}
        </Button>
      </DialogFooter>
    </Form>
  )
}
